import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import React, { useState } from "react";

import { bindArgs } from "@core/foundation/function";
import { isNullish } from "@core/foundation/types";

import {
  EmptyView,
  ErrorView,
  LoadingView,
} from "@react/foundation/placeholder";
import { Button } from "@react/ui-primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@react/ui-primitives/select";

import type { Project } from "@apis/memory/models";

import { useQueries } from "../../../../shared/hooks/useQueries.ts";
import { useMemoryActions } from "../../hooks/useMemoryActions.ts";

interface ConnectGithubRepoDialogContentProps {
  readonly project: Project;
}

export const ConnectGithubRepoDialogContent: FC<
  ConnectGithubRepoDialogContentProps
> = ({ project }) => {
  const [selectedGithubAccountId, setSelectedGithubAccountId] = useState<
    null | number
  >(null);

  const { getGithubConnectionsQueryOptions } = useQueries();
  const githubConnections = useQuery(getGithubConnectionsQueryOptions());

  const { addGithubRepoToProject } = useMemoryActions();

  const handleSelectGithubAccountId = (value: string): void => {
    setSelectedGithubAccountId(Number(value));
  };

  if (githubConnections.status === "pending") {
    return <LoadingView />;
  }

  if (githubConnections.status === "error") {
    return <ErrorView description="Failed to load repositories." />;
  }

  const { items } = githubConnections.data;

  const selectedItem =
    items.find(
      item => item.githubConnection.githubAccountId === selectedGithubAccountId,
    ) ?? items[0];

  if (isNullish(selectedItem)) {
    return (
      <EmptyView
        description="Connect a GitHub account to add repositories."
        title="No GitHub connections"
      />
    );
  }

  const connected = new Set(project.connectedGithubRepoIds);
  const availableRepos = selectedItem.repos.filter(
    repo => !connected.has(repo.githubRepoId),
  );

  return (
    <div className="flex flex-col gap-2">
      <Select
        onValueChange={handleSelectGithubAccountId}
        value={String(selectedItem.githubConnection.githubAccountId)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map(({ githubConnection }) => (
            <SelectItem
              key={githubConnection.githubAccountId}
              value={String(githubConnection.githubAccountId)}
            >
              {githubConnection.githubAccountLogin}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {availableRepos.length === 0 ? (
        <EmptyView
          description="There are no repositories left to connect."
          title="No repositories available"
        />
      ) : (
        availableRepos.map(repo => (
          <div
            className="flex items-center justify-between rounded-md border p-3"
            key={repo.githubRepoId}
          >
            <span className="text-sm">{repo.fullName}</span>
            <Button
              disabled={addGithubRepoToProject.isPending}
              onClick={bindArgs(addGithubRepoToProject.mutate, {
                githubAccountId: selectedItem.githubConnection.githubAccountId,
                githubRepoId: repo.githubRepoId,
                projectId: project.id,
              })}
            >
              Connect
            </Button>
          </div>
        ))
      )}
    </div>
  );
};
